// FinKloz — Cash Template Summary
// All-accounts overview table matching the "Summary" sheet in the Excel template.

import React from 'react';
import { sans, mono, green, greenLight, greenDark, red, redLight, amber, amberLight, useTheme } from '../tokens';

function fmt$(n) {
  if (n === null || n === undefined) return '--';
  if (n === 0) return '$0.00';
  const abs = Math.abs(n);
  const str = abs.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `$(${str})` : `$${str}`;
}

function StatusBadge({ status }) {
  const config = {
    reconciled: { bg: greenLight, fg: greenDark, label: 'Reconciled' },
    minor_diff: { bg: amberLight, fg: amber, label: '~ Minor Diff' },
    unreconciled: { bg: redLight, fg: red, label: 'Unreconciled' },
    in_progress: { bg: '#EDE9FE', fg: '#7C3AED', label: 'In Progress' },
    not_started: { bg: '#F5F5F5', fg: '#9E9E9E', label: 'Not Started' },
  };
  const c = config[status] || config.not_started;
  return (
    <span style={{
      fontFamily: sans, fontSize: 10, fontWeight: 600,
      padding: '2px 8px', borderRadius: 3,
      background: c.bg, color: c.fg,
    }}>
      {c.label}
    </span>
  );
}

export default function CashTemplateSummary({ templateData }) {
  const t = useTheme();

  if (!templateData) return null;

  return (
    <div>
      <div style={{ fontFamily: sans, fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 4 }}>
        {templateData.companyName}
      </div>
      <div style={{ fontFamily: sans, fontSize: 12, color: t.textSecondary, marginBottom: 16 }}>
        Bank Reconciliation -- Summary -- {templateData.month}
      </div>

      {/* Header row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '200px 40px 100px 100px 80px 90px 100px',
        gap: 6, padding: '6px 0',
        fontFamily: sans, fontSize: 10, fontWeight: 600, color: t.textMuted,
        borderBottom: `2px solid ${t.border}`,
      }}>
        <span>Account</span>
        <span>CCY</span>
        <span style={{ textAlign: 'right' }}>GL Ending (CAD)</span>
        <span style={{ textAlign: 'right' }}>Stmt Ending</span>
        <span style={{ textAlign: 'right' }}>Difference</span>
        <span style={{ textAlign: 'center' }}>Status</span>
        <span>Stmt Date</span>
      </div>

      {/* Data rows */}
      {templateData.summary.map(row => (
        <div key={row.accountNo} style={{
          display: 'grid',
          gridTemplateColumns: '200px 40px 100px 100px 80px 90px 100px',
          gap: 6, padding: '6px 0', alignItems: 'center',
          borderBottom: `1px solid ${t.borderLight}`,
        }}>
          <span style={{ fontFamily: sans, fontSize: 11, color: t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.accountNo} - {row.name}
          </span>
          <span style={{ fontFamily: mono, fontSize: 10, color: t.textSecondary }}>{row.currency}</span>
          <span style={{ fontFamily: mono, fontSize: 11, color: t.textPrimary, textAlign: 'right' }}>
            {fmt$(row.glEnding)}
          </span>
          <span style={{ fontFamily: mono, fontSize: 11, color: t.textPrimary, textAlign: 'right' }}>
            {fmt$(row.stmtEnding)}
          </span>
          <span style={{ fontFamily: mono, fontSize: 11, color: t.textPrimary, textAlign: 'right' }}>
            {typeof row.difference === 'number' ? fmt$(row.difference) : (row.difference || '--')}
          </span>
          <span style={{ textAlign: 'center' }}>
            <StatusBadge status={row.status} />
          </span>
          <span style={{ fontFamily: mono, fontSize: 10, color: t.textSecondary }}>
            {row.stmtDate || '--'}
          </span>
        </div>
      ))}

      {/* Notes */}
      <div style={{ marginTop: 16, fontFamily: sans, fontSize: 11, color: t.textSecondary }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Notes:</div>
        <div>For USD accounts, GL ending is CAD functional. Bank statement is in USD. See each tab for details.</div>
        <div style={{ marginTop: 2 }}>Generated by FinKloz.</div>
      </div>
    </div>
  );
}
